import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fifoService } from '../../services/fifoService';
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config/api';

const STATUS_CONFIG = {
    'Pending':      { bg: '#fff8e1', border: '#f59e0b', text: '#92400e', icon: 'bi-clock-history',      label: 'รอดำเนินการ' },
    'Sent to Qcm': { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', icon: 'bi-arrow-right-circle', label: 'ส่ง QCM แล้ว' },
    'At QCM':      { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', icon: 'bi-building',            label: 'ที่ QCM' },
    'Pass QC':     { bg: '#f0fdf4', border: '#22c55e', text: '#166534', icon: 'bi-check-circle-fill',   label: 'ผ่าน QC' },
    'Fail QC':     { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', icon: 'bi-x-circle-fill',       label: 'ไม่ผ่าน QC' },
    'Declined':    { bg: '#fdf4ff', border: '#a855f7', text: '#6b21a8', icon: 'bi-slash-circle',        label: 'ปฏิเสธ' },
    'Cancelled':   { bg: '#f1f5f9', border: '#64748b', text: '#334155', icon: 'bi-x-circle',             label: 'ยกเลิก' },
};

const getStatus = (status) => STATUS_CONFIG[status] || { bg: '#f9fafb', border: '#9ca3af', text: '#374151', icon: 'bi-circle', label: status || 'ไม่ระบุ' };

const formatDate = (val) => {
    if (!val) return '-';
    if (typeof val === 'string' && val.includes('/')) return val;
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
};

const AllRequests = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('allreq_view') || 'card');
    const itemsPerPage = viewMode === 'table' ? 25 : 12;

    useEffect(() => { fetchAllRequests(); }, []);
    useEffect(() => { setCurrentPage(1); }, [searchTerm, filter, startDate, endDate]);

    const fetchAllRequests = async () => {
        setLoading(true);
        try {
            const data = await fifoService.getAllRequests();
            setRequests(data);
        } catch (error) {
            toast.error('ไม่สามารถโหลดข้อมูลใบแจ้งงาน QC ได้');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = (documentId) => {
        // Public endpoint — no token needed, same as the static /pdfs/*.pdf files
        window.open(`${API_URL}/fifo/requests/${documentId}/pdf`, '_blank');
    };

    const handleCancel = async (documentId) => {
        if (!window.confirm(`ยืนยันยกเลิกใบแจ้งงาน ${documentId}? เลขที่เอกสารจะยังคงอยู่ แต่จะถูกทำเครื่องหมายว่ายกเลิกแล้ว`)) return;
        try {
            await fifoService.cancelRequest(documentId);
            toast.success('ยกเลิกใบแจ้งงานแล้ว');
            fetchAllRequests();
        } catch (error) {
            toast.error(error.response?.data?.error || 'ยกเลิกไม่สำเร็จ');
        }
    };

    const statusCounts = {
        all: requests.length,
        Pending: requests.filter(r => r.status === 'Pending').length,
        'Sent to Qcm': requests.filter(r => r.status === 'Sent to Qcm' || r.status === 'At QCM').length,
        'Pass QC': requests.filter(r => r.status === 'Pass QC').length,
        'Fail QC': requests.filter(r => r.status === 'Fail QC').length,
        'Cancelled': requests.filter(r => r.status === 'Cancelled').length,
    };

    const filteredRequests = requests.filter(req => {
        const matchesSearch = !searchTerm ||
            req.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.id?.toString().includes(searchTerm) ||
            req.items?.some(item => item.serial?.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesFilter = filter === 'all' ||
            (filter === 'Sent to Qcm' ? (req.status === 'Sent to Qcm' || req.status === 'At QCM') : req.status === filter);

        const matchesDateRange =
            (!startDate || new Date(req.createdAt) >= new Date(startDate)) &&
            (!endDate || new Date(req.createdAt) <= new Date(endDate + 'T23:59:59'));

        return matchesSearch && matchesFilter && matchesDateRange;
    });

    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedRequests = filteredRequests.slice(startIndex, startIndex + itemsPerPage);

    const handleExportExcel = () => {
        if (filteredRequests.length === 0) { toast.warning('ไม่มีข้อมูลสำหรับ Export'); return; }
        const excelData = filteredRequests.map(req => ({
            'รหัส': req.id,
            'วันที่สร้าง': formatDate(req.createdAt),
            'ชื่อลูกค้า': req.customerName || '-',
            'ที่อยู่': req.customerAddress || '-',
            'วันที่ต้องการ': formatDate(req.wantDate),
            'จำนวนเครื่อง': req.items?.length || 0,
            'Serial Numbers': req.items?.map(i => i.serial).join(', ') || '-',
            'รายการงาน': Array.isArray(req.jobDetails) ? req.jobDetails.join(', ') : '-',
            'หมายเหตุ': req.remark || '-',
            'สถานะ': getStatus(req.status).label,
        }));
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ใบแจ้งงาน QC');
        ws['!cols'] = [15, 20, 30, 40, 15, 12, 50, 30, 30, 15].map(wch => ({ wch }));
        XLSX.writeFile(wb, `ใบแจ้งงาน_QC_${new Date().toLocaleDateString('th-TH').replace(/\//g, '-')}.xlsx`);
        toast.success('Export Excel สำเร็จ!');
    };

    if (loading) return <PageLoader message="กำลังโหลดใบแจ้งงาน QC..." />;

    const FILTER_TABS = [
        { key: 'all',          label: 'ทั้งหมด',     icon: 'bi-list-ul',           color: '#0d6efd' },
        { key: 'Pending',      label: 'รอดำเนินการ', icon: 'bi-clock-history',     color: '#f59e0b' },
        { key: 'Sent to Qcm', label: 'ส่ง QCM แล้ว',icon: 'bi-arrow-right-circle',color: '#3b82f6' },
        { key: 'Pass QC',      label: 'ผ่าน QC',     icon: 'bi-check-circle-fill', color: '#22c55e' },
        { key: 'Fail QC',      label: 'ไม่ผ่าน QC',  icon: 'bi-x-circle-fill',     color: '#ef4444' },
        { key: 'Cancelled',    label: 'ยกเลิก',       icon: 'bi-x-circle',          color: '#64748b' },
    ];

    return (
        <div>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h4 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                        <i className="bi bi-file-earmark-text-fill me-2 text-primary"></i>
                        ใบแจ้งงาน QC ทั้งหมด
                    </h4>
                    <p className="text-muted mb-0 small">รายการใบแจ้งงาน QC ทั้งหมดในระบบ</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {/* View toggle */}
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            className={`btn ${viewMode === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => { setViewMode('card'); localStorage.setItem('allreq_view', 'card'); setCurrentPage(1); }}
                            title="มุมมองการ์ด"
                        >
                            <i className="bi bi-grid-3x3-gap-fill"></i>
                        </button>
                        <button
                            className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => { setViewMode('table'); localStorage.setItem('allreq_view', 'table'); setCurrentPage(1); }}
                            title="มุมมองตาราง"
                        >
                            <i className="bi bi-table"></i>
                        </button>
                    </div>
                    <button className="btn btn-outline-success btn-sm" onClick={handleExportExcel} disabled={filteredRequests.length === 0}>
                        <i className="bi bi-file-earmark-excel me-1"></i> Export Excel
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate('/request-machine')}>
                        <i className="bi bi-plus-circle me-1"></i> สร้างใบแจ้งงานใหม่
                    </button>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
                {FILTER_TABS.map(tab => {
                    const active = filter === tab.key;
                    const count = statusCounts[tab.key] ?? 0;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            style={{
                                border: `1.5px solid ${active ? tab.color : '#e2e8f0'}`,
                                background: active ? tab.color : '#fff',
                                color: active ? '#fff' : '#475569',
                                borderRadius: '8px',
                                padding: '6px 14px',
                                fontSize: '13px',
                                fontWeight: active ? 600 : 400,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.15s',
                            }}
                        >
                            <i className={`bi ${tab.icon}`}></i>
                            {tab.label}
                            <span style={{
                                background: active ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                                color: active ? '#fff' : '#64748b',
                                borderRadius: '20px',
                                padding: '1px 7px',
                                fontSize: '11px',
                                fontWeight: 600,
                            }}>{count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Search + Date */}
            <div className="d-flex gap-2 mb-4 flex-wrap align-items-end">
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="ค้นหาชื่อลูกค้า, Serial, รหัสเอกสาร..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="btn btn-outline-secondary" onClick={() => setSearchTerm('')}>
                                <i className="bi bi-x"></i>
                            </button>
                        )}
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2 border rounded-2 bg-white px-2" style={{ height: '31px' }}>
                    <i className="bi bi-calendar3 text-muted small"></i>
                    <input
                        type="date"
                        className="form-control form-control-sm border-0 p-0 shadow-none"
                        style={{ width: '125px' }}
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                    />
                    <span className="text-muted small">ถึง</span>
                    <input
                        type="date"
                        className="form-control form-control-sm border-0 p-0 shadow-none"
                        style={{ width: '125px' }}
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                    />
                </div>
                {(searchTerm || startDate || endDate || filter !== 'all') && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => { setSearchTerm(''); setStartDate(''); setEndDate(''); setFilter('all'); }}>
                        <i className="bi bi-arrow-clockwise me-1"></i>รีเซ็ต
                    </button>
                )}
                <span className="text-muted small ms-auto align-self-center">{filteredRequests.length} รายการ</span>
            </div>

            {/* Content */}
            {paginatedRequests.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    {searchTerm || filter !== 'all' || startDate || endDate ? 'ไม่พบรายการที่ค้นหา' : 'ยังไม่มีใบแจ้งงาน QC'}
                </div>
            ) : viewMode === 'card' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
                    {paginatedRequests.map(request => {
                        const st = getStatus(request.status);
                        return (
                            <div key={request.id} style={{
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                borderTop: `3px solid ${st.border}`,
                                padding: '16px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                            }}>
                                {/* Card Header */}
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="fw-bold" style={{ fontSize: '13px', color: '#64748b' }}>{request.id}</div>
                                        <div className="fw-bold" style={{ fontSize: '15px', color: '#1e293b' }}>
                                            {request.customerName || 'ไม่ระบุชื่อลูกค้า'}
                                        </div>
                                    </div>
                                    <span style={{
                                        background: st.bg,
                                        border: `1px solid ${st.border}`,
                                        color: st.text,
                                        borderRadius: '20px',
                                        padding: '3px 10px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}>
                                        <i className={`bi ${st.icon} me-1`}></i>{st.label}
                                    </span>
                                </div>

                                {/* Info rows */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '13px' }}>
                                    {request.customerAddress && (
                                        <div className="text-muted" style={{ fontSize: '12px' }}>
                                            <i className="bi bi-geo-alt me-1"></i>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '280px', verticalAlign: 'bottom' }}>
                                                {request.customerAddress}
                                            </span>
                                        </div>
                                    )}
                                    <div className="d-flex gap-3">
                                        <div style={{ color: '#475569' }}>
                                            <i className="bi bi-calendar-event me-1 text-muted"></i>
                                            สร้าง: <span className="fw-medium">{formatDate(request.createdAt)}</span>
                                        </div>
                                        <div style={{ color: '#475569' }}>
                                            <i className="bi bi-calendar-check me-1 text-muted"></i>
                                            ต้องการ: <span className="fw-medium">{formatDate(request.wantDate)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Serials */}
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>SERIAL NUMBERS</span>
                                        <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: 700 }}>
                                            {request.items?.length || 0} รายการ
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {request.items?.slice(0, 4).map((item, idx) => (
                                            <span key={idx} style={{
                                                background: '#f1f5f9',
                                                color: '#334155',
                                                borderRadius: '6px',
                                                padding: '2px 8px',
                                                fontSize: '11px',
                                                fontFamily: 'monospace',
                                            }}>
                                                {item.serial}
                                            </span>
                                        ))}
                                        {request.items?.length > 4 && (
                                            <span style={{ fontSize: '11px', color: '#94a3b8', alignSelf: 'center' }}>
                                                +{request.items.length - 4} เพิ่มเติม
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Job Details */}
                                {Array.isArray(request.jobDetails) && request.jobDetails.length > 0 && (
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>รายละเอียดงาน</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {request.jobDetails.slice(0, 3).map((job, idx) => (
                                                <span key={idx} style={{
                                                    background: '#f0fdf4',
                                                    color: '#166534',
                                                    border: '1px solid #bbf7d0',
                                                    borderRadius: '6px',
                                                    padding: '2px 8px',
                                                    fontSize: '11px',
                                                }}>
                                                    {job}
                                                </span>
                                            ))}
                                            {request.jobDetails.length > 3 && (
                                                <span style={{ fontSize: '11px', color: '#94a3b8', alignSelf: 'center' }}>
                                                    +{request.jobDetails.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="d-flex gap-2 pt-1" style={{ borderTop: '1px solid #f1f5f9', marginTop: 'auto' }}>
                                    <button
                                        className="btn btn-sm btn-outline-primary flex-grow-1"
                                        onClick={() => navigate(`/request-detail/${request.id}`)}
                                    >
                                        <i className="bi bi-eye me-1"></i>ดูรายละเอียด
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDownloadPDF(request.id)}
                                        title="ดู PDF"
                                    >
                                        <i className="bi bi-file-pdf"></i>
                                    </button>
                                    {!['Cancelled', 'Pass QC', 'Fail QC'].includes(request.status) && (
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => handleCancel(request.id)}
                                            title="ยกเลิก"
                                        >
                                            <i className="bi bi-x-circle"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* Table View */
                <div style={{ overflowX: 'auto' }}>
                    <table className="table table-hover" style={{ fontSize: '13px', background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                        <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <tr>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px', whiteSpace: 'nowrap' }}>รหัส</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px' }}>ลูกค้า</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px', whiteSpace: 'nowrap' }}>วันที่สร้าง</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px', whiteSpace: 'nowrap' }}>ต้องการ</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px', whiteSpace: 'nowrap' }}>เครื่อง</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px' }}>Serial Numbers</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px', whiteSpace: 'nowrap' }}>สถานะ</th>
                                <th style={{ fontWeight: 600, color: '#64748b', padding: '10px 12px', textAlign: 'center' }}>การดำเนินการ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedRequests.map(request => {
                                const st = getStatus(request.status);
                                return (
                                    <tr key={request.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <td style={{ padding: '10px 12px', color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                            {request.id}
                                        </td>
                                        <td style={{ padding: '10px 12px', maxWidth: '200px' }}>
                                            <div className="fw-semibold" style={{ color: '#1e293b' }}>{request.customerName || 'ไม่ระบุ'}</div>
                                            {request.customerAddress && (
                                                <div className="text-muted" style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>
                                                    {request.customerAddress}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#475569' }}>{formatDate(request.createdAt)}</td>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#475569' }}>{formatDate(request.wantDate)}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                            <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
                                                {request.items?.length || 0}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxWidth: '220px' }}>
                                                {request.items?.slice(0, 3).map((item, idx) => (
                                                    <span key={idx} style={{ background: '#f1f5f9', color: '#334155', borderRadius: '4px', padding: '1px 6px', fontSize: '11px', fontFamily: 'monospace' }}>
                                                        {item.serial}
                                                    </span>
                                                ))}
                                                {request.items?.length > 3 && (
                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{request.items.length - 3}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                            <span style={{
                                                background: st.bg, border: `1px solid ${st.border}`, color: st.text,
                                                borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: 600,
                                            }}>
                                                <i className={`bi ${st.icon} me-1`}></i>{st.label}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => navigate(`/request-detail/${request.id}`)}>
                                                <i className="bi bi-eye"></i>
                                            </button>
                                            <button className="btn btn-sm btn-outline-danger me-1" onClick={() => handleDownloadPDF(request.id)} title="ดู PDF">
                                                <i className="bi bi-file-pdf"></i>
                                            </button>
                                            {!['Cancelled', 'Pass QC', 'Fail QC'].includes(request.status) && (
                                                <button className="btn btn-sm btn-outline-secondary" onClick={() => handleCancel(request.id)} title="ยกเลิก">
                                                    <i className="bi bi-x-circle"></i>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <span className="text-muted small">
                        แสดง {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredRequests.length)} จาก {filteredRequests.length} รายการ
                    </span>
                    <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        {[...Array(totalPages)].map((_, i) => {
                            const p = i + 1;
                            if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                                return (
                                    <button key={p} className={`btn btn-sm ${currentPage === p ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setCurrentPage(p)}>
                                        {p}
                                    </button>
                                );
                            } else if (p === currentPage - 2 || p === currentPage + 2) {
                                return <span key={p} className="btn btn-sm disabled">…</span>;
                            }
                            return null;
                        })}
                        <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllRequests;
